import { DataType } from '../types'

export const DEFAULT_MIN_SETTEMP = 16
export const DEFAULT_MAX_SETTEMP = 30

export interface RawRealtimeSensorLike {
    ObjectId?: number | null
    SensorId?: number
    DataTypeId?: number
    objectId?: number | null
    sensorId?: number
    dataTypeId?: number
}

export interface DataPointMeasurementLike {
    actuator?: {
        ActuatorId: number
        DataTypeId: number
        Min?: number
        Max?: number
    }
    sensorId: number
}

export interface ZoneCommandCapability {
    objectId: number
    driveActuatorId?: number
    setTempActuatorId?: number
    setTempMin?: number
    setTempMax?: number
}

export interface EquipmentActuatorLike {
    ActuatorId?: number
    DataTypeId?: number
    Min?: number | null
    Max?: number | null
}

export interface EquipmentLike {
    Actuators?: EquipmentActuatorLike[]
}

export interface GetEquipmentsResponse {
    equipments?: EquipmentLike[]
}

/**
 * Single source of truth for "what can this zone be commanded with".
 * Built from /realtime/equipments, which is the only endpoint that reliably
 * exposes actuators: the familytree payload no longer carries a usable sensor
 * list on sites whose state is rebuilt from the object snapshot.
 */
export const getZoneCommandCapabilityFromEquipments = (
    objectId: number,
    equipments: EquipmentLike[] = []
): ZoneCommandCapability => {
    const actuators = equipments.flatMap((equipment) => equipment?.Actuators || [])
    const driveActuator = actuators.find(
        (actuator) => actuator?.DataTypeId === DataType.STATE && actuator?.ActuatorId
    )
    const setTempActuator = actuators.find(
        (actuator) => actuator?.DataTypeId === DataType.SETPOINT_TEMPERATURE && actuator?.ActuatorId
    )

    return {
        objectId,
        driveActuatorId: driveActuator?.ActuatorId,
        setTempActuatorId: setTempActuator?.ActuatorId,
        setTempMin: setTempActuator?.Min ?? DEFAULT_MIN_SETTEMP,
        setTempMax: setTempActuator?.Max ?? DEFAULT_MAX_SETTEMP,
    }
}

const COMMANDABLE_DATA_TYPES = [DataType.STATE, DataType.SETPOINT_TEMPERATURE]

export const getRawSensorObjectId = (sensor: RawRealtimeSensorLike) => sensor.ObjectId ?? sensor.objectId ?? null

export const getRawSensorId = (sensor: RawRealtimeSensorLike) => sensor.SensorId ?? sensor.sensorId

export const getRawSensorDataTypeId = (sensor: RawRealtimeSensorLike) => sensor.DataTypeId ?? sensor.dataTypeId

export const getCommandableSensorIdsForObjects = (rawSensors: RawRealtimeSensorLike[], objectIds: number[]) => {
    const objectIdsSet = new Set(objectIds)
    const sensorIds = new Set<number>()

    rawSensors.forEach((sensor) => {
        const objectId = getRawSensorObjectId(sensor)
        const sensorId = getRawSensorId(sensor)
        const dataTypeId = getRawSensorDataTypeId(sensor)

        if (!objectId || !sensorId || !dataTypeId) {
            return
        }
        if (!objectIdsSet.has(objectId) || !COMMANDABLE_DATA_TYPES.includes(dataTypeId as DataType)) {
            return
        }

        sensorIds.add(sensorId)
    })

    return Array.from(sensorIds)
}

export const extractZoneCommandCapabilities = (
    measurements: DataPointMeasurementLike[] = [],
    rawSensors: RawRealtimeSensorLike[] = [],
    objectIds: number[]
): ZoneCommandCapability[] => {
    const capabilitiesMap = new Map<number, ZoneCommandCapability>(
        objectIds.map((objectId) => [
            objectId,
            {
                objectId,
            },
        ])
    )

    const sensorObjectMap = new Map<number, number>()
    rawSensors.forEach((sensor) => {
        const sensorId = getRawSensorId(sensor)
        const objectId = getRawSensorObjectId(sensor)

        if (!sensorId || !objectId || !capabilitiesMap.has(objectId)) {
            return
        }

        sensorObjectMap.set(sensorId, objectId)
    })

    measurements.forEach((measurement) => {
        const objectId = sensorObjectMap.get(measurement.sensorId)
        const actuator = measurement.actuator

        if (!objectId || !actuator) {
            return
        }

        const capability = capabilitiesMap.get(objectId)
        if (!capability) {
            return
        }

        if (actuator.DataTypeId === DataType.STATE) {
            capability.driveActuatorId = actuator.ActuatorId
        }

        if (actuator.DataTypeId === DataType.SETPOINT_TEMPERATURE) {
            capability.setTempActuatorId = actuator.ActuatorId
            capability.setTempMin = actuator.Min ?? DEFAULT_MIN_SETTEMP
            capability.setTempMax = actuator.Max ?? DEFAULT_MAX_SETTEMP
        }
    })

    return Array.from(capabilitiesMap.values())
}

export const getSharedSetpointRange = (capabilities: ZoneCommandCapability[]) => {
    const setTempCapabilities = capabilities.filter((capability) => capability.setTempActuatorId)

    if (setTempCapabilities.length === 0) {
        return null
    }

    const min = Math.max(
        ...setTempCapabilities.map((capability) => capability.setTempMin ?? DEFAULT_MIN_SETTEMP)
    )
    const max = Math.min(
        ...setTempCapabilities.map((capability) => capability.setTempMax ?? DEFAULT_MAX_SETTEMP)
    )

    if (min > max) {
        return null
    }

    return { min, max }
}
